import React, { useCallback, useEffect, useMemo, useState } from "react";

import { AHText, Box, Input } from "arkh";

import {
  NativeStackNavigationProp,
  createNativeStackNavigator,
} from "@react-navigation/native-stack";
import { CompositeNavigationProp, RouteProp } from "@react-navigation/native";

import { graphql, useLazyLoadQuery, useMutation } from "react-relay";

import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorScreenProps,
} from "../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";

import { useIntlTokens } from "../../../components/mobile/useIntlTokens";

import {
  useNavigationHeader,
  useNavigationHeaderOptions,
} from "../../../common/navigationHeaderOptions";
import { useIntl } from "react-intl";
import { OrgAdminTopicsEditQuery } from "./__generated__/OrgAdminTopicsEditQuery.graphql";

import { StoreSelector } from "../WorkspaceEntity/StoreSelector";
import {
  ReceiverStoreAllTeamsIndicator,
  ReceiverStoreMain,
  ReceiverStoreNoTeamsIndicator,
  ReceiverStoreTeamsIndicator,
} from "../WorkspaceEntity/ReceiverStoresSelector";
import { OrgAdminTopicsEditMutation } from "./__generated__/OrgAdminTopicsEditMutation.graphql";
import { SegmentedControlNew } from "@/src/components/SegmentedControlNew/SegmentedControlNew";
import { OrgAdminTeamSelector } from "../OrgAdmin/OrgAdminTeams/OrgAdminTeamsSelector";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { OrgAdminTopicsEditDeleteMutation } from "./__generated__/OrgAdminTopicsEditDeleteMutation.graphql";

type ParamList = {
  OrgAdminTopicsEditScreen: {
    topicId: string;
  };
};

const Stack = createNativeStackNavigator<ParamList>();

export const OrgAdminTopicsEdit = withWebModal(
  (props: UserLoggedInNavigatorScreenProps<"OrgAdminTopicsEdit">) => {
    const getOptions = useNavigationHeaderOptions();
    return (
      <Stack.Navigator>
        <Stack.Screen
          name="OrgAdminTopicsEditScreen"
          component={OrgAdminTopicsEditScreen}
          initialParams={{
            topicId: props.route.params.topicId,
          }}
          options={({ navigation }) =>
            getOptions({
              type: "CREATE",
              onCancel: () => {
                navigation.goBack();
              },
              title: "",
              onSave: () => {},
              saveLoading: false,
              disabled: true,
            })
          }
        />
      </Stack.Navigator>
    );
  }
);

type OrgUserInviteScreenProps = {
  route: RouteProp<ParamList, "OrgAdminTopicsEditScreen">;
  navigation: CompositeNavigationProp<
    UserLoggedInNavigatorNavigationProp<"OrgAdminTopicsEdit">,
    NativeStackNavigationProp<ParamList, "OrgAdminTopicsEditScreen">
  >;
};

function OrgAdminTopicsEditScreen(props: OrgUserInviteScreenProps) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => {
        return <OrgAdminTopicsEditScreenC {...props} fetchKey={fetchKey} />;
      }}
    </LazyQueryNetworkBoundrary>
  );
}

function OrgAdminTopicsEditScreenC(
  props: OrgUserInviteScreenProps & { fetchKey: string }
) {
  const intl_tokens = useIntlTokens();

  const intl = useIntl();
  const [selectedType, setSelectedType] = useState<"STORES" | "TEAMS">(
    "STORES"
  );
  const [fk, setFk] = useState<string>(props.fetchKey);

  const data = useLazyLoadQuery<OrgAdminTopicsEditQuery>(
    graphql`
      query OrgAdminTopicsEditQuery($topicId: ID!) {
        organisation {
          topic(id: $topicId) {
            name
            description
            teams {
              id
            }
            isDefault
            isPrivate

            hasAllStores
            allPotentialReceiverStores {
              store {
                id
                ...StoreSelector_store
                ...ReceiverStoresSelectorMain_receiverStore
              }
            }
            allPotentialTeams {
              id
              ...OrgAdminTeamsSelector_teams
              ...ReceiverStoresSelectorTeamsIndicator_teams
              store {
                id
              }
            }

            allReceiverStores {
              store {
                id
              }
              routedTeams {
                id
              }
            }
          }
        }
      }
    `,
    {
      topicId: props.route.params.topicId,
    },
    {
      fetchPolicy: "store-and-network",
      fetchKey: fk + props.fetchKey,
    }
  );

  const [save, saveLoading] = useMutation<OrgAdminTopicsEditMutation>(graphql`
    mutation OrgAdminTopicsEditMutation(
      $topicId: ID!
      $name: String!
      $description: String!
      $receivers: SetTeamsRoutingInput
    ) {
      organisation {
        topic(id: $topicId) {
          setReceivers(input: $receivers) {
            teams {
              id
            }
            allReceiverStores {
              store {
                id
              }
            }
          }
        }
      }
      organisation {
        topic(id: $topicId) {
          setDescription(setDescription: $description, setName: $name) {
            id
            description
            name
          }
        }
      }
    }
  `);

  const [deleteMutation,] =
    useMutation<OrgAdminTopicsEditDeleteMutation>(graphql`
      mutation OrgAdminTopicsEditDeleteMutation($topicId: ID!) {
        organisation {
          topic(id: $topicId) {
            archiveTopic {
              id
            }
          }
        }
      }
    `);

  const stores = data.organisation?.topic?.allPotentialReceiverStores ?? [];

  const remoteRoutedTeamIds = useMemo(() => {
    return data.organisation?.topic?.teams?.map((t) => t.id) ?? [];
  }, [data.organisation?.topic?.teams]);

  const [selectedRoutedTeamIds, setSelectedRoutedTeamIds] =
    useState<string[]>(remoteRoutedTeamIds);

  const remoteSelectedStoreIds = useMemo(
    () =>
      data.organisation?.topic?.allReceiverStores?.map(
        (receiverStore) => receiverStore.store.id
      ) ?? [],
    [data.organisation?.topic?.allReceiverStores]
  );
  const hasAllStores = data.organisation?.topic?.hasAllStores ?? false;

  useEffect(() => {
    setSelectedStoreIds(remoteSelectedStoreIds);
    setSelectedRoutedTeamIds(remoteRoutedTeamIds);
  }, [remoteSelectedStoreIds, remoteRoutedTeamIds]);

  const remoteName = data.organisation?.topic?.name ?? "";
  const remoteDescription = data.organisation?.topic?.description ?? "";

  const [name, setName] = useState<string>(remoteName);
  const [description, setDescription] = useState<string>(remoteDescription);
  const [selectedStoreIds, setSelectedStoreIds] = useState<string[]>(
    remoteSelectedStoreIds
  );

  const allStoreTeams = useMemo(
    () =>
      data.organisation?.topic?.allPotentialTeams.filter((team) =>
        selectedStoreIds.includes(team.store.id)
      ) ?? [],
    [data.organisation?.topic?.allPotentialTeams, selectedStoreIds]
  );

  // Create optimized lookup maps
  const receiverStoreById = useMemo(() => {
    const map = new Map();
    data.organisation?.topic?.allPotentialReceiverStores.forEach(
      (receiverStore) => {
        map.set(receiverStore.store.id, receiverStore);
      }
    );
    return map;
  }, [data.organisation?.topic?.allPotentialReceiverStores]);

  const teamsPerStore = useMemo(() => {
    const map = new Map();
    allStoreTeams.forEach((team) => {
      if (selectedRoutedTeamIds.includes(team.id)) {
        const teams = map.get(team.store.id) || [];
        teams.push(team);
        map.set(team.store.id, teams);
      }
    });
    return map;
  }, [allStoreTeams, selectedRoutedTeamIds]);

  const isSelectedStoreDirty = useMemo(() => {
    if (
      !hasAllStores &&
      selectedStoreIds.length !== remoteSelectedStoreIds.length
    ) {
      return true;
    }
    if (
      remoteSelectedStoreIds.some(
        (storeId) => !selectedStoreIds.includes(storeId)
      )
    ) {
      return true;
    }
    return false;
  }, [hasAllStores, selectedStoreIds, remoteSelectedStoreIds]);

  const isSelectedRoutedTeamDirty = useMemo(() => {
    if (selectedRoutedTeamIds.length !== remoteRoutedTeamIds.length) {
      return true;
    }
    if (
      remoteRoutedTeamIds.some(
        (teamId) => !selectedRoutedTeamIds.includes(teamId)
      )
    ) {
      return true;
    }

    return false;
  }, [selectedRoutedTeamIds, remoteRoutedTeamIds]);

  const isDirty = useMemo(() => {
    if (isSelectedStoreDirty) {
      return true;
    }
    if (isSelectedRoutedTeamDirty) {
      return true;
    }
    if (name !== remoteName) {
      return true;
    }
    if (description !== remoteDescription) {
      return true;
    }

    return false;
  }, [
    isSelectedStoreDirty,
    isSelectedRoutedTeamDirty,
    name,
    remoteName,
    description,
    remoteDescription,
  ]);
  const onSave = useCallback(() => {
    save({
      variables: {
        topicId: props.route.params.topicId,
        name,
        description,
        receivers:
          isSelectedStoreDirty || isSelectedRoutedTeamDirty
            ? {
                storeIds: hasAllStores ? undefined : selectedStoreIds,
                teamIds: selectedRoutedTeamIds,
              }
            : null,
      },
    });
  }, [
    save,
    props.route.params.topicId,
    name,
    description,
    isSelectedStoreDirty,
    isSelectedRoutedTeamDirty,
    hasAllStores,
    selectedStoreIds,
    selectedRoutedTeamIds,
  ]);

  const disabled = name.trim() === "" || !isDirty;

  useNavigationHeader({
    type: "SAVE",
    title: intl.formatMessage({
      defaultMessage: "Edit Topic",
      description: "OrgAdminTopicsEdit title",
    }),

    defaultContentStyle: true,
    onCancel: () => {
      props.navigation.goBack();
    },

    disabled,
    onSave,
    more:
      data.organisation?.topic?.isDefault ||
      data.organisation?.topic?.hasAllStores
        ? []
        : [
            {
              label: intl_tokens.Delete,
              icon: "trash",
              color: "negative",
              type: "button",
              onPress() {
                deleteMutation({
                  onCompleted: () => {
                    props.navigation.goBack();
                  },
                  variables: {
                    topicId: props.route.params.topicId,
                  },
                });
              },
            },
          ],
    saveLoading: saveLoading,
  });

  return (
    <Box space="medium" style={{ flex: 1 }} gap="medium">
      <Input
        value={name}
        onChangeText={setName}
        placeholder={intl_tokens.Name}
        textContentType="name"
      />

      <Input
        value={description}
        onChangeText={setDescription}
        placeholder={intl_tokens.Description}
        textContentType="name"
        multiline
      />

      <SegmentedControlNew
        items={[
          {
            children: <AHText type="subtitle">{intl_tokens.Units}</AHText>,
            width: 100,
            value: "STORES",
          },
          {
            width: 100,
            children: <AHText type="subtitle">{intl_tokens.Teams}</AHText>,
            value: "TEAMS",
          },
        ]}
        selected={selectedType}
        setSelected={(selected) => {
          setSelectedType(selected);
          setFk(props.fetchKey + selected);
        }}
      />

      {selectedType === "STORES" ? (
        <Box
          style={{ flex: 1, overflow: "hidden", borderWidth: 1 }}
          cornerRadius="large"
          borderColor="border-primary"
        >
          <StoreSelector
            disabled={hasAllStores}
            stores={stores.map((store) => store.store)}
            selectedStoreIds={selectedStoreIds}
            onStoreSetSelect={setSelectedStoreIds}
            renderItem={(store, selected, index) => {
              const receiverStore = receiverStoreById.get(store.id);

              if (!receiverStore) {
                return null;
              }

              const teams = selected ? teamsPerStore.get(store.id) || [] : [];

              return (
                <ReceiverStoreMain
                  store={receiverStore.store}
                  right={
                    selected ? (
                      <>
                        {teams.length === 0 ? (
                          data.organisation?.topic?.isPrivate ? (
                            <ReceiverStoreNoTeamsIndicator />
                          ) : (
                            <ReceiverStoreAllTeamsIndicator />
                          )
                        ) : (
                          <ReceiverStoreTeamsIndicator teams={teams} />
                        )}
                      </>
                    ) : null
                  }
                  selected={selected}
                  isLast={index === stores.length - 1}
                  onPress={() => {
                    setSelectedStoreIds(
                      selected
                        ? selectedStoreIds.filter((id) => id !== store.id)
                        : [...selectedStoreIds, store.id]
                    );
                  }}
                  disabled={hasAllStores}
                />
              );
            }}
          />
        </Box>
      ) : (
        <Box style={{ flex: 1, overflow: "hidden" }}>
          <OrgAdminTeamSelector
            teams={allStoreTeams}
            selectedTeams={selectedRoutedTeamIds}
            setSelectedTeams={setSelectedRoutedTeamIds}
          />
        </Box>
      )}
    </Box>
  );
}
